import { useShow, useNavigation } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Card,
  Descriptions,
  Tag,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Popconfirm,
  message,
  Input,
  Modal,
} from 'antd';
import {
  CheckOutlined,
  StopOutlined,
  CarOutlined,
  ShoppingOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { apiClient } from '../../lib/api-client';

interface BusCompany {
  id: number;
  name: string;
  legalName: string;
  businessForm: string;
  inn: string;
  legalAddress: string;
  actualAddress: string;
  bankAccount: string;
  bankName: string;
  info: string | null;
  directorFullName: string;
  directorPassportNumber: string;
  directorPassportScan: string | null;
  contactPhone: string;
  contactEmail: string | null;
  contractNumber: string | null;
  contractScan: string | null;
  commissionPercent: number;
  username: string;
  bonus: number;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED';
  blockedReason: string | null;
  balance: number;
  createdAt: string;
  updatedAt: string;
  _count: {
    busTrips: number;
  };
  stats: {
    totalTrips: number;
    totalBookings: number;
    totalRevenue: number;
  };
}

export const BusCompanyShow = () => {
  const { list } = useNavigation();
  const { queryResult } = useShow<BusCompany>();
  const { data, isLoading, refetch } = queryResult;
  const company = data?.data;

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);

  const handleBlock = async () => {
    setBlocking(true);
    try {
      await apiClient.post(`/admin/bus/companies/${company?.id}/block`, {
        reason: blockReason,
      });
      message.success('Kompaniya bloklandi');
      setBlockModalOpen(false);
      setBlockReason('');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async () => {
    try {
      await apiClient.post(`/admin/bus/companies/${company?.id}/unblock`);
      message.success('Kompaniya faollashtirildi');
      refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
  };

  const statusColors: Record<string, string> = {
    ONLINE: 'green',
    OFFLINE: 'orange',
    BLOCKED: 'red',
  };

  const statusLabels: Record<string, string> = {
    ONLINE: 'Faol',
    OFFLINE: 'Oflayn',
    BLOCKED: 'Bloklangan',
  };

  return (
    <>
      <Show
        isLoading={isLoading}
        title="Avtobus kompaniyasi"
        headerButtons={
          <Space>
            {company?.status === 'BLOCKED' ? (
              <Popconfirm
                title="Kompaniyani faollashtirasizmi?"
                onConfirm={handleUnblock}
              >
                <Button type="primary" icon={<CheckOutlined />}>
                  Faollashtirish
                </Button>
              </Popconfirm>
            ) : (
              <Button
                danger
                icon={<StopOutlined />}
                onClick={() => setBlockModalOpen(true)}
              >
                Bloklash
              </Button>
            )}
            <Button onClick={() => list('admin/bus/companies')}>
              Orqaga
            </Button>
          </Space>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <Card>
              <div className="text-center" style={{ marginBottom: 16, textAlign: 'center' }}>
                <h3 style={{ marginBottom: 4 }}>{company?.name}</h3>
                <p style={{ color: '#7F8C8D', marginBottom: 8 }}>{company?.legalName}</p>
                <Tag color={statusColors[company?.status || 'OFFLINE']}>
                  {statusLabels[company?.status || 'OFFLINE']}
                </Tag>
              </div>

              <Descriptions column={1} size="small">
                <Descriptions.Item label="Telefon">{company?.contactPhone}</Descriptions.Item>
                <Descriptions.Item label="Email">{company?.contactEmail || '-'}</Descriptions.Item>
                <Descriptions.Item label="Login">{company?.username}</Descriptions.Item>
                <Descriptions.Item label="Komissiya">{company?.commissionPercent}%</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Statistika" style={{ marginTop: 24 }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Reyslar"
                    value={company?.stats?.totalTrips || company?._count?.busTrips || 0}
                    prefix={<CarOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Buyurtmalar"
                    value={company?.stats?.totalBookings || 0}
                    prefix={<ShoppingOutlined />}
                  />
                </Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Statistic
                    title="Balans"
                    value={formatCurrency(company?.balance || 0)}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Daromad"
                    value={formatCurrency(company?.stats?.totalRevenue || 0)}
                    prefix={<DollarOutlined />}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Card title="Yuridik ma'lumotlar" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Shakli">
                      {company?.businessForm || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="STIR (INN)">
                      {company?.inn || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Bank hisobi">
                      {company?.bankAccount || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Bank nomi">
                      {company?.bankName || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Yuridik manzil">
                      {company?.legalAddress || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Haqiqiy manzil">
                      {company?.actualAddress || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card title="Direktor" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="F.I.O">
                      {company?.directorFullName || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Pasport">
                      {company?.directorPassportNumber || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
                <Card title="Shartnoma" size="small" style={{ marginTop: 16 }}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Raqam">
                      {company?.contractNumber || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ro'yxatdan o'tgan">
                      {company?.createdAt
                        ? new Date(company.createdAt).toLocaleDateString('uz-UZ')
                        : '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
                {company?.blockedReason && (
                  <Card title="Bloklash sababi" size="small" style={{ marginTop: 16 }}>
                    <p style={{ color: '#ff4d4f' }}>{company.blockedReason}</p>
                  </Card>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Show>

      <Modal
        title="Kompaniyani bloklash"
        open={blockModalOpen}
        onCancel={() => {
          setBlockModalOpen(false);
          setBlockReason('');
        }}
        onOk={handleBlock}
        okText="Bloklash"
        cancelText="Bekor qilish"
        okButtonProps={{ danger: true, loading: blocking }}
      >
        <p style={{ marginBottom: 16 }}>Bloklash sababini kiriting:</p>
        <Input.TextArea
          value={blockReason}
          onChange={(e) => setBlockReason(e.target.value)}
          placeholder="Bloklash sababi..."
          rows={4}
        />
      </Modal>
    </>
  );
};
